import { useTranslation } from "react-i18next";
import type { DailyCost } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate, formatUsd } from "@/i18n/format";

/** Day-by-day cost bars; the data is also in a visually hidden table for screen readers. */
export function CostTrend({ days }: Readonly<{ days: DailyCost[] }>) {
  const { t } = useTranslation("adminUsage");
  const max = Math.max(0, ...days.map((day) => day.costUsd));
  return (
    <PageSection title={t("costs.trend")}>
      {days.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("costs.trendEmpty")}
        </Text>
      ) : (
        <>
          <div className="flex h-32 items-end gap-0.5" aria-hidden="true">
            {days.map((day) => (
              <div
                key={day.date}
                title={`${formatDate(day.date)}: ${formatUsd(day.costUsd)}`}
                className="min-w-0 flex-1 rounded-t-sm bg-brand-gradient"
                style={{ height: `${max > 0 ? Math.max(2, (day.costUsd / max) * 100) : 2}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between" aria-hidden="true">
            <Text as="span" variant="caption" tone="tertiary">
              {formatDate(days[0]?.date ?? "")}
            </Text>
            <Text as="span" variant="caption" tone="tertiary">
              {formatDate(days.at(-1)?.date ?? "")}
            </Text>
          </div>
          <table className="sr-only">
            <caption>{t("costs.trendTable")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("costs.trendDate")}</th>
                <th scope="col">{t("costs.trendCost")}</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day.date}>
                  <td>{formatDate(day.date)}</td>
                  <td>{formatUsd(day.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </PageSection>
  );
}
